import { STAFF_TYPES, type StaffType } from "odyssey-shared";
import { timeoutSignal } from "../../timeout-signal.ts";

/**
 * The page's side of `POST /staff/badge` (ADR-112 decision 5.5, P1.9-T121): a JSON body of one field, `{ hidden }`, with the page's current session token as a bearer token and no cookie, the answer read field by field. What
 * the page cannot read (a fetch that rejects, a gateway from before the route, a body that is no JSON or out of shape) is
 * a failed change, never an answer.
 */

/**
 * What a staff account wears: the types its live roles hold, the one it wears (always the roles' default) and whether it is
 * hidden, as the gateway's `staffBadgeOffer` and the route's answer both give it.
 */
export interface BadgeOffer {
  readonly types: readonly StaffType[];
  readonly wears: StaffType;
  readonly hidden: boolean;
}

/**
 * A change: whether to hide the badge. The type worn is the roles' and is never chosen (ADR-112 decision 5.5, amended
 * 2026-10-16).
 */
export type BadgeChoice = { readonly hidden: boolean };

/**
 * What came of a change: the offer after it; the session ended (401); the roles do not allow it (403); over a limit (429),
 * with the seconds to wait when the gateway said; the database could not save it (503); or a failure the page could not read.
 */
export type BadgeReply = { readonly kind: "ok"; readonly offer: BadgeOffer } | { readonly kind: "rate"; readonly waitSeconds: number | null } | { readonly kind: "session" | "forbidden" | "unavailable" | "failed" };

/**
 * One change.
 */
export type BadgeChange = (choice: BadgeChoice) => Promise<BadgeReply>;

const REQUEST_TIMEOUT_MS = 10_000;
const BEARER = "Bearer";

/**
 * Reads an offer against its shape.
 *
 * @param value The parsed body or message.
 * @returns The offer, or null when a field is missing or of another type, or the type worn is not among the types held.
 */
export function readBadgeOffer(value: unknown): BadgeOffer | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const { types, wears, hidden } = value as Record<string, unknown>;
  const known = (type: unknown): type is StaffType => typeof type === "string" && (STAFF_TYPES as readonly string[]).includes(type);
  if (!Array.isArray(types) || types.length === 0 || !types.every(known) || !known(wears) || typeof hidden !== "boolean") {
    return null;
  }
  return { types: [...types], wears, hidden };
}

/**
 * Builds the client.
 *
 * @param options The gateway's `/staff/badge` URL, the page's current session token (null when it holds none), and the
 *   fetch, the page's own unless a test gives another.
 * @returns The change; it never rejects.
 */
export function badgeClient(options: { readonly endpoint: string; readonly token: () => string | null; readonly fetch?: (input: string, init?: RequestInit) => Promise<Response> }): BadgeChange {
  const send = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));
  return async (choice) => {
    const token = options.token();
    if (token === null || token === "") {
      return { kind: "session" };
    }
    const limit = timeoutSignal(REQUEST_TIMEOUT_MS);
    try {
      const response = await send(options.endpoint, { method: "POST", headers: { Authorization: [BEARER, token].join(" "), "Content-Type": "application/json" }, body: JSON.stringify(choice), credentials: "omit", signal: limit.signal });
      if (response.status === 200) {
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          return { kind: "failed" };
        }
        const offer = readBadgeOffer(body);
        return offer === null ? { kind: "failed" } : { kind: "ok", offer };
      }
      if (response.status === 429) {
        const wait = Number(response.headers.get("Retry-After"));
        return { kind: "rate", waitSeconds: Number.isFinite(wait) && wait > 0 ? Math.ceil(wait) : null };
      }
      const kinds: Readonly<Record<number, "session" | "forbidden" | "unavailable">> = { 401: "session", 403: "forbidden", 503: "unavailable" };
      return { kind: kinds[response.status] ?? "failed" };
    } catch {
      return { kind: "failed" };
    } finally {
      limit.settle();
    }
  };
}
